import { useEffect } from "react";
import {
  DMSans_400Regular,
  DMSans_500Medium,
  DMSans_700Bold,
  useFonts as useDMSansFonts,
} from "@expo-google-fonts/dm-sans";
import {
  Fraunces_600SemiBold,
  Fraunces_700Bold,
  useFonts as useFrauncesFonts,
} from "@expo-google-fonts/fraunces";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Stack } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import * as Sentry from "@sentry/react-native";
import Toast from "react-native-toast-message";

import { AppLifecycle, useStartupUpdateGate } from "~/lib/app-updates";
import { BackendStatusOverlay } from "~/components/BackendStatusOverlay";
import { MfaGate } from "~/components/composed/MfaGate";
import { QuerySessionBoundary } from "~/components/QuerySessionBoundary";
import { AuthProvider } from "~/hooks/useAuth";
import { queryClient } from "~/lib/query-client";
import { persistOptions } from "~/lib/query-persist";
import { ThemeProvider, useTheme } from "~/styles/theme";
import "~/utils/web-alert";

void SplashScreen.preventAutoHideAsync();

function RootLayout() {
  const [frauncesLoaded, frauncesError] = useFrauncesFonts({
    Fraunces_600SemiBold,
    Fraunces_700Bold,
  });
  const [dmSansLoaded, dmSansError] = useDMSansFonts({
    DMSans_400Regular,
    DMSans_500Medium,
    DMSans_700Bold,
  });
  const fontsReady =
    (frauncesLoaded && dmSansLoaded) || Boolean(frauncesError || dmSansError);

  // Hold the splash until fonts load and the launch-time update check finishes (4s cap).
  const updateCheckDone = useStartupUpdateGate();
  const ready = fontsReady && updateCheckDone;

  useEffect(() => {
    if (ready) void SplashScreen.hideAsync();
  }, [ready]);

  if (!ready) return null;

  return (
    <ThemeProvider>
      <AppLifecycle />
      <BackendStatusOverlay />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
        onSuccess={() => {
          // Replay any writes that were queued offline in a previous session.
          void queryClient.resumePausedMutations();
        }}
      >
        <AuthProvider>
          <MfaGate>
            <QuerySessionBoundary>
              <ThemedStack />
            </QuerySessionBoundary>
          </MfaGate>
        </AuthProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
}

function ThemedStack() {
  const { scheme, colors } = useTheme();
  return (
    <>
      <Stack
        screenOptions={{
          headerShown: false,
          headerStyle: { backgroundColor: colors.primary },
          contentStyle: {
            backgroundColor: colors.background,
          },
        }}
      />
      <StatusBar style={scheme === "dark" ? "light" : "dark"} />
      <Toast />
    </>
  );
}

export default Sentry.wrap(RootLayout);
